
import { supabase } from '@/integrations/supabase/client';

// Encryption utility for Microsoft tokens
export class TokenEncryption {
  private static async getEncryptionKey(): Promise<string> {
    // In production, this would use a proper key management system
    // For now, we'll use a Supabase secret
    const { data, error } = await supabase.functions.invoke('get-encryption-key');
    if (error) {
      throw new Error('Failed to get encryption key');
    }
    return data.key;
  }

  private static async encryptData(data: string, key: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(key.slice(0, 32)); // AES-256 requires 32-byte key
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV for AES-GCM
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encoder.encode(data)
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...combined));
  }

  private static async decryptData(encryptedData: string, key: string): Promise<string> {
    const combined = new Uint8Array(
      atob(encryptedData)
        .split('')
        .map(char => char.charCodeAt(0))
    );

    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const encoder = new TextEncoder();
    const keyData = encoder.encode(key.slice(0, 32));
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encrypted
    );

    return new TextDecoder().decode(decrypted);
  }

  static async encryptToken(token: string): Promise<string> {
    try {
      const key = await this.getEncryptionKey();
      return await this.encryptData(token, key);
    } catch (error) {
      console.error('Token encryption failed:', error);
      throw new Error('Failed to encrypt token');
    }
  }

  static async decryptToken(encryptedToken: string): Promise<string> {
    try {
      const key = await this.getEncryptionKey();
      return await this.decryptData(encryptedToken, key);
    } catch (error) {
      console.error('Token decryption failed:', error);
      throw new Error('Failed to decrypt token');
    }
  }

  static async encryptCredentials(credentials: {
    access_token: string;
    refresh_token: string;
  }): Promise<{
    encrypted_access_token: string;
    encrypted_refresh_token: string;
  }> {
    const [encryptedAccessToken, encryptedRefreshToken] = await Promise.all([
      this.encryptToken(credentials.access_token),
      this.encryptToken(credentials.refresh_token)
    ]);

    return {
      encrypted_access_token: encryptedAccessToken,
      encrypted_refresh_token: encryptedRefreshToken
    };
  }

  static async decryptCredentials(credentials: {
    encrypted_access_token: string;
    encrypted_refresh_token: string;
  }): Promise<{
    access_token: string;
    refresh_token: string;
  }> {
    const [accessToken, refreshToken] = await Promise.all([
      this.decryptToken(credentials.encrypted_access_token),
      this.decryptToken(credentials.encrypted_refresh_token)
    ]);

    return {
      access_token: accessToken,
      refresh_token: refreshToken
    };
  }
}
